import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CheckCircle, XCircle, Eye, EyeOff, Zap, Award, Flag, ChevronRight, RotateCcw, Share2, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { ShareCardButton } from "../components/ShareCard";
import { useAuth } from "../App";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export function SectionQuiz({
  section, sectionId, quizAnswers, handleQuizAnswer, shuffledOptions,
  quizSubmitted, quizResults, submitting, submitQuiz, resetQuiz,
  showQuizResult, showMascotReaction, showXpReward, showBadgeUnlock, showAdvanceBtn,
  mascotMood, showAnswers, setShowAnswers, retryCount, xpAlreadyEarned,
  onBackToLesson, navigate
}) {
  const { user } = useAuth();
  const isFree = user?.purchase_tier === "free";
  return (
    <motion.div
      key="quiz"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-8">
        <button 
          onClick={onBackToLesson}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft size={18} /> Back to lesson
        </button>
        <h2 className="font-heading text-3xl font-bold text-white mb-2">Section {sectionId} Quiz</h2>
        <p className="text-zinc-500">Answer correctly to earn BTC XP. Minimum 2 of 3 to pass.</p>
        <p className="text-zinc-600 text-xs mt-1">
          Perfect score (3/3) = advance immediately. Lower scores require review time.
        </p>
      </div>

      <div className="space-y-6">
        {section.quiz_questions.map((question, qIndex) => {
          const options = shuffledOptions[qIndex] || [question.correct, ...question.wrong];
          const selectedAnswer = quizAnswers[qIndex];
          const isCorrectAnswer = quizSubmitted && showAnswers && selectedAnswer === question.correct;
          const isWrongAnswer = quizSubmitted && showAnswers && selectedAnswer && selectedAnswer !== question.correct;
          
          return (
            <motion.div 
              key={qIndex} 
              className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6"
              initial={false}
              animate={quizSubmitted && showAnswers ? { 
                borderColor: isCorrectAnswer ? "rgba(34,197,94,0.3)" : isWrongAnswer ? "rgba(239,68,68,0.3)" : "rgba(255,255,255,0.1)"
              } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <p className="text-white font-medium mb-4">{qIndex + 1}. {question.question}</p>
              <div className="space-y-3">
                {options.map((option, oIndex) => {
                  const isSelected = selectedAnswer === option;
                  const showCorrect = quizSubmitted && showAnswers && option === question.correct;
                  const showWrong = quizSubmitted && showAnswers && isSelected && option !== question.correct;
                  
                  return (
                    <motion.button
                      key={oIndex}
                      onClick={() => handleQuizAnswer(qIndex, option)}
                      disabled={quizSubmitted}
                      className={`w-full p-4 rounded-lg border text-left transition-colors duration-500 ${
                        showCorrect
                          ? "border-green-500 bg-green-500/20 text-green-400"
                          : showWrong
                            ? "border-red-500 bg-red-500/20 text-red-400"
                            : isSelected
                              ? "border-[#F7931A] bg-[#F7931A]/10 text-white"
                              : "border-white/10 bg-[#161616] text-zinc-300 hover:border-[#F7931A]/50"
                      }`}
                      initial={false}
                      animate={quizSubmitted && showAnswers ? { 
                        opacity: 1,
                        transition: { duration: 0.5, delay: 0.5 }
                      } : {}}
                      data-testid={`quiz-option-${qIndex}-${oIndex}`}
                    >
                      <span className="flex items-center gap-3">
                        {showCorrect && <CheckCircle size={18} className="flex-shrink-0" />}
                        {showWrong && <XCircle size={18} className="flex-shrink-0" />}
                        {option}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Quiz Actions */}
      <div className="mt-8 space-y-4">
        {!quizSubmitted ? (
          <Button
            onClick={submitQuiz}
            disabled={submitting || Object.keys(quizAnswers).length < 3}
            className="w-full btn-primary text-lg py-4"
            data-testid="submit-quiz-btn"
          >
            {submitting ? "Submitting..." : "Submit Quiz"}
          </Button>
        ) : (
          <>
            <QuizResultBanner showQuizResult={showQuizResult} quizResults={quizResults} retryCount={retryCount} xpAlreadyEarned={xpAlreadyEarned} />
            <AnswersToggle showQuizResult={showQuizResult} showAnswers={showAnswers} setShowAnswers={setShowAnswers} />
            <MascotReaction showMascotReaction={showMascotReaction} mascotMood={mascotMood} />
            <XpRewardBanner showXpReward={showXpReward} quizResults={quizResults} />
            <BadgeUnlock showBadgeUnlock={showBadgeUnlock} />
            <QuizActions
              showAdvanceBtn={showAdvanceBtn} quizResults={quizResults}
              sectionId={sectionId} section={section} navigate={navigate}
              resetQuiz={resetQuiz} onBackToLesson={onBackToLesson} isFree={isFree}
            />
          </>
        )}
      </div>
    </motion.div>
  );
}

function QuizResultBanner({ showQuizResult, quizResults, retryCount, xpAlreadyEarned }) {
  return (
    <AnimatePresence>
      {showQuizResult && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className={`p-6 rounded-xl ${quizResults?.passed ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}
        >
          <div className="flex items-center gap-3 mb-2">
            {quizResults?.passed ? <CheckCircle className="text-green-400" size={24} /> : <XCircle className="text-red-400" size={24} />}
            <h3 className={`font-heading text-xl font-bold ${quizResults?.passed ? 'text-green-400' : 'text-red-400'}`}>
              {quizResults?.passed ? "Quiz Passed!" : "Keep Learning"}
            </h3>
          </div>
          <p className="text-zinc-300">
            You got {quizResults?.correct} of {quizResults?.total} correct.
            {retryCount > 0 && xpAlreadyEarned && quizResults?.passed && (
              <span className="text-zinc-500 text-sm block mt-1">BTC XP already earned on previous attempt.</span>
            )}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AnswersToggle({ showQuizResult, showAnswers, setShowAnswers }) {
  return (
    <AnimatePresence>
      {showQuizResult && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <Button
            onClick={() => setShowAnswers(!showAnswers)}
            variant="ghost"
            className="w-full text-zinc-400 hover:text-white border border-white/10"
            data-testid="toggle-answers-btn"
          >
            {showAnswers ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
            {showAnswers ? "Hide Answers" : "Show Answers"}
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MascotReaction({ showMascotReaction, mascotMood }) {
  return (
    <AnimatePresence>
      {showMascotReaction && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: "easeOut" }} className="flex justify-center">
          <motion.div animate={mascotMood === "happy" ? { y: [0, -8, 0], transition: { duration: 0.6 } } : { y: [0, 4, 0], transition: { duration: 0.8 } }}>
            <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className={`w-16 h-16 rounded-xl ${mascotMood === "happy" ? "btc-glow" : "opacity-60 grayscale-[30%]"}`} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function XpRewardBanner({ showXpReward, quizResults }) {
  return (
    <AnimatePresence>
      {showXpReward && quizResults?.xp_earned > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="flex items-center justify-center gap-2 py-3 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-xl"
        >
          <Zap className="text-[#F7931A]" size={20} />
          <span className="text-[#F7931A] font-mono font-bold text-lg">+{quizResults.xp_earned} BTC XP earned!</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BadgeUnlock({ showBadgeUnlock }) {
  return (
    <AnimatePresence>
      {showBadgeUnlock && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          className="flex items-center justify-center gap-2 py-3 bg-purple-500/10 border border-purple-500/30 rounded-xl"
        >
          <Award className="text-purple-400" size={20} />
          <span className="text-purple-400 font-medium">New badge unlocked!</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function QuizActions({ showAdvanceBtn, quizResults, sectionId, section, navigate, resetQuiz, onBackToLesson, isFree }) {
  return (
    <AnimatePresence>
      {showAdvanceBtn && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-3">
          <div className="flex gap-4">
            {quizResults?.passed ? (
              <>
                <Button onClick={() => navigate("/course")} className="flex-1 btn-secondary" data-testid="finish-quiz-btn">
                  <Flag size={16} className="mr-2" /> Finish
                </Button>
                <Button
                  onClick={() => isFree ? navigate("/pricing") : navigate(parseInt(sectionId) < 21 ? `/course/section/${parseInt(sectionId) + 1}` : "/course")}
                  className="flex-1 btn-primary" data-testid="next-lesson-btn"
                >
                  {isFree ? (
                    <><Lock size={16} className="mr-2" /> Unlock Full Course</>
                  ) : (
                    <>{parseInt(sectionId) < 21 ? "Next Lesson" : "Back to Course"} <ChevronRight size={18} className="ml-1" /></>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onBackToLesson} className="flex-1 btn-secondary" data-testid="review-lesson-btn">Review Lesson</Button>
                <Button onClick={resetQuiz} className="flex-1 btn-primary" data-testid="retry-quiz-btn">
                  <RotateCcw size={16} className="mr-2" /> Retry
                </Button>
              </>
            )}
          </div>
          {quizResults?.passed && (
            <div className="flex justify-center">
              <ShareCardButton
                type="quiz_result"
                data={{
                  title: `Section ${sectionId}: ${section?.title || ""}`,
                  subtitle: "Quiz Complete",
                  correct: quizResults.correct,
                  total: quizResults.total,
                  xp: quizResults.xp_earned || 0
                }}
                shareText={`I scored ${quizResults.correct}/${quizResults.total} on "${section?.title}" — Section ${sectionId} of Bitcoin Brain! Learn Bitcoin the right way.`}
                shareUrl="https://bitcoinbrain.space/demo"
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
